"use client";

import { useEffect, useState } from "react";
import {
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
} from "@heroui/navbar";
import { Avatar } from "@heroui/avatar";
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem
} from "@heroui/dropdown"; // Added Dropdown components
import clsx from "clsx";
import { siteConfig } from "@/config/site";
import NextLink from "next/link";
import { link as linkStyles } from "@heroui/theme";
import { getUserDataForNavbar, UserDataForNavbar } from '@/api/getUser';
import { useRouter, usePathname } from "next/navigation";

const HeroNavbar = () => {
    const pathname = usePathname();
    const router = useRouter();
    const [user, setUser] = useState<UserDataForNavbar | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const apiKey = localStorage.getItem("api_key");
            if (!apiKey) {
                router.push("/");
                return;
            }

            try {
                const userDataFromSQL = await getUserDataForNavbar(apiKey);
                if (!userDataFromSQL) {
                    router.push("/");
                } else {
                    setUser(userDataFromSQL);
                }
            } catch (error) {
                console.error("Failed to fetch user:", error);
                router.push("/");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [router]);

    // Handle the actual logout logic
    const handleLogout = () => {
        localStorage.removeItem("api_key");
        router.push("/");
    };

    if (loading) return null;

    const getNavbarBgColor = (user: number) => {
        switch (user) {
            case 1:
                return "bg-red-300/85";
            case 3:
                return "bg-[#DBFFF5]/75";
            case 2:
            default:
                return "bg-[#DBF0FF]/85";
        }
    };
    const navbarColorClass = getNavbarBgColor(user?.role_id as number);


    const getNavbarBgClasses = (user: number) => {
        switch (user) {
            case 1:
                return `w-[18vw] border-1 border-black  h-[75vh] rounded-4xl items-start z-10 ${navbarColorClass}`;
            case 3:
                return `w-[16vw] border-1 border-black  h-[70vh] rounded-4xl items-start z-10 ${navbarColorClass}`;
            case 2:
            default:
                return `w-[18vw] border-1 border-black  h-[75vh] rounded-4xl items-start z-10  ${navbarColorClass}`;
        }
    };

    const getNavbarBgSize = (user: number) => {
        switch (user) {
            case 1:
                return ["flex flex-col gap-0 mt-[71vh]", "flex items-center mr-5 justify-center text-lg"];
            case 3:
                return ["flex flex-col gap-0 mt-[60vh]", "flex mr-[4vw] item-center justify-center text-lg"];
            case 2:
            default:
                return ["flex flex-col gap-0 mt-[71vh]", "flex items-center mr-5 justify-center text-lg"];
        }
    };

    const navbarBgClass = getNavbarBgClasses(user?.role_id as number);
    const navbarSizeClass = getNavbarBgSize(user?.role_id as number);


    return (
        <div className={clsx("relative  mt-[5vh] ml-[5vh] p-0 rounded-4xl justify-center text-2xl w-[20vw] h-[90vh]-max h-[70]")}>
            <Navbar isBordered isBlurred classNames={{

                base: `${navbarBgClass}`,
                content: "gap-4 text-black "
            }}>
                <div className={navbarSizeClass[0]}>
                    <div className="h-[10vh] w-[17vw]">
                        <NavbarBrand className="ml-[10vh] w-m pt-1 p-0">
                            <h1 className={clsx("text-black text-4xl font-bold")}>ÁVI</h1>
                        </NavbarBrand>
                    </div>

                    <hr className="mt-[1vh] mb-[6vh] text-black p-0 w-[27vh] " />

                    <NavbarContent>
                        <ul className="lg:flex flex-col w-full gap-12 justify-end">
                            {siteConfig.navItems.map((item) => {
                                const isActive = pathname === item.href;

                                if (item.href === "/manage_users" && user?.role_id !== 1 || item.href === "/results" && user?.role_id !== 3 ) {
                                    return null;
                                }
                                if (item.href === "/questions" && user?.role_id !== 1 && user?.role_id !== 2) {
                                    return null;
                                }

                                return (
                                    <NavbarItem key={item.href}>
                                        <NextLink
                                            className={clsx(
                                                linkStyles({ color: "black" }),
                                                "ml-4",
                                                isActive ? "font-bold" : "font-normal",
                                            )}
                                            href={item.href}
                                        >
                                            {item.label}
                                        </NextLink>
                                    </NavbarItem>
                                );
                            })}

                            <hr className="mt-[2vh] mb-[0.5vh] text-black ml-0 p-0 w-[27vh] "/>


                            <NavbarItem className={navbarSizeClass[1]}>
                                <Dropdown placement="top-start">
                                    <DropdownTrigger>
                                        <div className="flex items-center gap-3 cursor-pointer p-1 rounded-lg hover:bg-black/5 transition-colors">
                                            <Avatar
                                                src={user?.pfp}
                                                size="sm"
                                                className="w-8 h-8 text-tiny"
                                                isBordered
                                                color={user?.role_id == 1 ? "danger" : user?.role_id == 2 ? "warning" : "success"}
                                            />
                                            <span className="font-medium text-black">
                                                {user?.username}
                                            </span>
                                        </div>
                                    </DropdownTrigger>
                                    <DropdownMenu aria-label="User Actions" variant="flat">
                                        <DropdownItem key="profile" className="h-14 gap-2">
                                            <p className="font-semibold">Bejelentkezve, mint</p>
                                            <p className="font-semibold">{user?.username}</p>
                                        </DropdownItem>
                                        <DropdownItem
                                            key="logout"
                                            color="danger"
                                            className="text-danger"
                                            onPress={handleLogout}
                                        >
                                            Kijelentkezes
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarItem>
                        </ul>
                    </NavbarContent>
                </div>
            </Navbar>
        </div>
    );
};
export default HeroNavbar